/** /ai-changes — AI changes (backend metaplan A3c, AIE §10).
 *
 * Every change set the change engine writes — an MCP or console-agent apply, a Design Import
 * apply, a translation sync, or a revert of another change set — is one row: who made it and
 * how, what it changed, what it cost, and whether a record it changed was changed again since
 * (the revert would then meet conflicts). A row opens the change set's page, where it is
 * reviewed record by record and reverted.
 *
 * Filters live in the URL (linkable, preset-covered): tenant (console scope aware), user,
 * channel, status, MCP client, the created window and "changed since"; the URL-only
 * `revert_of` lists the reverts of one change set, and `batch_id` one content apply's change sets
 * (one per record: client MCP plan §4.7 "Batches"), which a row's Batch cell links. Gated by
 * `console_ai_changes_view` (Ops Admin only by default). */
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as aiChangesApi from '@/api/aiChanges';
import {
  AI_CHANGE_CHANNELS,
  AI_CHANGE_STATUSES,
  aiChangeKeys,
  channelLabel,
  shortId,
  statusColor,
  statusLabel,
  type AIChangeListItem,
} from '@/api/aiChanges';
import { formatUsd } from '@/api/aiSpend';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { UserPicker } from '@/components/UserPicker/UserPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import {
  actorActions,
  aiChangeHref,
  batchActions,
  batchHref,
  changeSetActions,
  channelActions,
  clientActions,
  statusActions,
  tenantGrid,
} from './aiChangeLinks';

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);
/** An ISO instant or a relative token (`now-7d`) — the backend reads both. */
const isoOrDrop = (v: string) => (paramToBound(v) ? v : undefined);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A comma list kept to the known values — an unknown one would 400 the whole grid. */
const knownList =
  (known: readonly { value: string }[]) =>
  (v: string): string | undefined => {
    const values = v.split(',').filter((x) => known.some((k) => k.value === x));
    return values.length ? values.join(',') : undefined;
  };

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'actor', toApi: digits },
  { param: 'actor_email', column: { field: 'actor', operator: 'contains' } },
  {
    param: 'channel',
    toApi: knownList(AI_CHANGE_CHANNELS),
    column: { field: 'channel', operator: 'is' },
  },
  {
    param: 'status',
    toApi: knownList(AI_CHANGE_STATUSES),
    column: { field: 'status', operator: 'is' },
  },
  { param: 'client', column: { field: 'client', operator: 'contains' } },
  { param: 'from', apiParam: 'created_after', toApi: isoOrDrop },
  { param: 'to', apiParam: 'created_before', toApi: isoOrDrop },
  {
    param: 'has_conflicts',
    toApi: (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    column: { field: 'has_conflicts', operator: 'is' },
  },
  // URL-only: "Reverts of this change set" from a change set's page.
  { param: 'revert_of', toApi: (v) => (UUID_RE.test(v) ? v : undefined) },
  // URL-only: "Change sets of this batch" from a row's Batch cell or a change set's page.
  { param: 'batch_id', toApi: (v) => (UUID_RE.test(v) ? v : undefined) },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'created_ts',
  'tenant',
  'channel',
  'status',
  'actor',
  'client',
  'reason',
  'summary',
  'entry_count',
  'cost_usd',
  'has_conflicts',
  'revert_of',
  'reverted_by',
  'batch_id',
  'id',
];
const DEFAULT_HIDDEN = ['id'];

const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');
const containsOperator = getGridStringOperators().filter((op) => op.value === 'contains');
const count = new Intl.NumberFormat('en-US');

const CONFLICT_OPTIONS = [
  { value: 'true', label: 'Changed since' },
  { value: 'false', label: 'Not changed since' },
];

/** A grid cell link that navigates by itself (cmd-click opens a tab) instead of the row. */
function CellLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={to} underline="hover" onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

/** A filter's text, committed on Enter or blur — not a request per keystroke. Adopts the URL's
 *  value when it changes underneath (reset, a preset, a link). */
function CommitTextField({
  label,
  value,
  placeholder,
  width,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder: string;
  width: number;
  onCommit: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value);
  }
  const commit = () => {
    const next = draft.trim();
    if (next !== value) onCommit(next || null);
  };
  return (
    <TextField
      size="small"
      label={label}
      value={draft}
      placeholder={placeholder}
      sx={{ width }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
    />
  );
}

export function AIChangesPage() {
  const navigate = useNavigate();

  const presets = useGridPresets({
    grid: 'ai_changes',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const actorRaw = getParam('actor');
  const actorId = actorRaw && /^\d+$/.test(actorRaw) ? Number(actorRaw) : null;
  const channels = (getParam('channel') ?? '').split(',').filter(Boolean);
  const statuses = (getParam('status') ?? '').split(',').filter(Boolean);
  const from = getParam('from');
  const to = getParam('to');
  const conflicts = getParam('has_conflicts') ?? '';
  const revertOf = getParam('revert_of');
  const batchId = getParam('batch_id');

  const columns = useMemo<GridColDef<AIChangeListItem>[]>(
    () => [
      {
        field: 'created_ts',
        headerName: 'When',
        width: 150,
        filterable: false,
        renderCell: (p) => (
          <Tooltip title={formatDate(p.row.created_ts)} describeChild>
            <span>{formatRelativeTime(p.row.created_ts)}</span>
          </Tooltip>
        ),
      },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.tenant?.code || row.tenant_code,
        renderCell: (p) =>
          p.row.tenant ? (
            <WithTenantMark environment={p.row.tenant.environment}>
              <ValueActions actions={operatorActions(p.row.tenant, tenantGrid(p.row.tenant.id))}>
                {p.row.tenant.code || p.row.tenant.name}
              </ValueActions>
            </WithTenantMark>
          ) : (
            p.row.tenant_code || '—'
          ),
      },
      {
        field: 'channel',
        headerName: 'Channel',
        width: 150,
        sortable: false,
        type: 'singleSelect',
        valueOptions: AI_CHANGE_CHANNELS.map((c) => ({ value: c.value, label: c.label })),
        filterOperators: isOperator,
        renderCell: (p) => (
          <ValueActions chip actions={channelActions(p.row.channel)}>
            <Chip size="small" variant="outlined" label={channelLabel(p.row.channel)} />
          </ValueActions>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        sortable: false,
        type: 'singleSelect',
        valueOptions: AI_CHANGE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
        filterOperators: isOperator,
        renderCell: (p) => (
          <ValueActions chip actions={statusActions(p.row.status)}>
            <Chip
              size="small"
              color={statusColor(p.row.status)}
              label={statusLabel(p.row.status)}
            />
          </ValueActions>
        ),
      },
      {
        field: 'actor',
        headerName: 'By',
        width: 200,
        sortable: false,
        filterOperators: containsOperator,
        valueGetter: (_, row) => row.actor?.email ?? '',
        renderCell: (p) =>
          p.row.actor ? (
            <ValueActions actions={actorActions(p.row.actor)}>{p.row.actor.email}</ValueActions>
          ) : (
            <Typography variant="body2" component="span" color="text.secondary">
              System
            </Typography>
          ),
      },
      {
        field: 'client',
        headerName: 'MCP client',
        width: 170,
        sortable: false,
        filterOperators: containsOperator,
        valueGetter: (_, row) => row.client?.name ?? '',
        renderCell: (p) =>
          p.row.client ? (
            <ValueActions actions={clientActions(p.row.client)}>
              {p.row.client.name || p.row.client.id}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'reason',
        headerName: 'Reason',
        flex: 1,
        minWidth: 180,
        sortable: false,
        filterable: false,
      },
      {
        field: 'summary',
        headerName: 'Summary',
        flex: 1,
        minWidth: 220,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <CellLink to={aiChangeHref(p.row.id)}>
            {p.row.summary || `Change set ${shortId(p.row.id)}`}
          </CellLink>
        ),
      },
      {
        field: 'entry_count',
        headerName: 'Records',
        width: 100,
        type: 'number',
        filterable: false,
        valueFormatter: (value: number) => count.format(value),
      },
      {
        field: 'cost_usd',
        headerName: 'Cost',
        width: 100,
        type: 'number',
        filterable: false,
        valueFormatter: (value: number) => formatUsd(value),
      },
      {
        field: 'has_conflicts',
        headerName: 'Changed since',
        width: 150,
        sortable: false,
        type: 'singleSelect',
        valueOptions: CONFLICT_OPTIONS,
        filterOperators: isOperator,
        valueGetter: (_, row) => (row.has_conflicts === null ? '' : String(row.has_conflicts)),
        renderCell: (p) =>
          p.row.has_conflicts ? (
            <Tooltip
              describeChild
              title="A record it changed was changed again after the apply — a revert may meet conflicts."
            >
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                icon={<WarningAmberIcon />}
                label="Changed since"
              />
            </Tooltip>
          ) : p.row.has_conflicts === false ? (
            <Typography variant="body2" component="span" color="text.secondary">
              No
            </Typography>
          ) : (
            '—'
          ),
      },
      {
        field: 'revert_of',
        headerName: 'Reverts',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.revert_of ? (
            <ValueActions actions={changeSetActions(p.row.revert_of)}>
              <CellLink to={aiChangeHref(p.row.revert_of)}>
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {shortId(p.row.revert_of)}
                </Box>
              </CellLink>
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'reverted_by',
        headerName: 'Reverted by',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.reverted_by ? (
            <ValueActions actions={changeSetActions(p.row.reverted_by.id)}>
              <CellLink to={aiChangeHref(p.row.reverted_by.id)}>
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {shortId(p.row.reverted_by.id)}
                </Box>
              </CellLink>
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'batch_id',
        headerName: 'Batch',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.batch_id ? (
            <ValueActions actions={batchActions(p.row.batch_id)}>
              <CellLink to={batchHref(p.row.batch_id)}>
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {shortId(p.row.batch_id)}
                </Box>
              </CellLink>
            </ValueActions>
          ) : (
            '—'
          ),
      },
      copyableColumn({
        field: 'id',
        headerName: 'ID',
        width: 300,
        sortable: false,
        filterable: false,
      }),
    ],
    [],
  );

  const listParams = (params: ResourceDataGridParams): aiChangesApi.AIChangeQuery => ({
    ...(params.extra as aiChangesApi.AIChangeFilters),
    ...(params.search ? { search: params.search } : {}),
    ordering: params.ordering ?? presets.ordering,
    ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
  });

  const queryFn = (params: ResourceDataGridParams) =>
    aiChangesApi.list({
      ...listParams(params),
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ...(params.countMode ? { count_mode: params.countMode } : {}),
    });

  const countOnly = (params: ResourceDataGridParams) => aiChangesApi.count(listParams(params));

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'when',
      priority: 7,
      active: Boolean(from || to),
      node: (
        <TimeWindowPicker
          label="Created"
          allowOpenEnded
          value={[paramToBound(from), paramToBound(to, { end: true })]}
          onChange={([start, end]) => setParams({ from: boundToParam(start), to: boundToParam(end) })}
        />
      ),
    },
    {
      key: 'tenant',
      priority: 6,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'channel',
      priority: 5,
      active: channels.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="Channel"
          value={channels}
          onChange={(e) =>
            setParam('channel', (e.target.value as unknown as string[]).join(',') || null)
          }
          sx={{ width: 180 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => (sel as string[]).map(channelLabel).join(', '),
          }}
        >
          {AI_CHANGE_CHANNELS.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              <Checkbox size="small" checked={channels.includes(c.value)} sx={{ py: 0 }} />
              <ListItemText primary={c.label} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'status',
      priority: 4,
      active: statuses.length > 0,
      node: (
        <TextField
          select
          size="small"
          label="Status"
          value={statuses}
          onChange={(e) =>
            setParam('status', (e.target.value as unknown as string[]).join(',') || null)
          }
          sx={{ width: 180 }}
          SelectProps={{
            multiple: true,
            renderValue: (sel) => (sel as string[]).map(statusLabel).join(', '),
          }}
        >
          {AI_CHANGE_STATUSES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              <Checkbox size="small" checked={statuses.includes(s.value)} sx={{ py: 0 }} />
              <ListItemText primary={s.label} />
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'conflicts',
      priority: 3,
      active: conflicts !== '',
      node: (
        <TextField
          select
          size="small"
          label="Changed since"
          value={conflicts}
          onChange={(e) => setParam('has_conflicts', e.target.value || null)}
          sx={{ width: 170 }}
        >
          <MenuItem value="">Any</MenuItem>
          {CONFLICT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'user',
      priority: 2,
      active: actorId !== null,
      node: (
        <Box sx={{ minWidth: 240 }}>
          <UserPicker
            value={actorId !== null ? { id: actorId, email: `user #${actorId}` } : null}
            onChange={(u) => setParam('actor', u ? String(u.id) : null)}
          />
        </Box>
      ),
    },
    {
      key: 'client',
      priority: 1,
      active: Boolean(getParam('client')),
      node: (
        <CommitTextField
          label="MCP client"
          placeholder="Client id or name"
          width={200}
          value={getParam('client') ?? ''}
          onCommit={(v) => setParam('client', v)}
        />
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="ai-changes"
        title="AI changes"
        subtitle="Every change set an AI client, a design import or a translation sync applied — who, how, what it changed — and its revert."
        actions={
          <Button component={RouterLink} to="/ai-changes/pending" variant="outlined" size="small">
            Pending MCP changes
          </Button>
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {revertOf && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setParam('revert_of', null)}>
          Reverts of change set{' '}
          <Link component={RouterLink} to={aiChangeHref(revertOf)} underline="hover">
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {revertOf}
            </Box>
          </Link>
        </Alert>
      )}

      {batchId && UUID_RE.test(batchId) && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setParam('batch_id', null)}>
          Change sets of batch{' '}
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {batchId}
          </Box>{' '}
          — one content apply, one change set per record.
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<AIChangeListItem>
          queryKey={aiChangeKeys.list}
          queryFn={queryFn}
          countMode={{ grid: 'ai_changes', countOnly }}
          allowExtendedTimeout
          resetPageOnQueryChange
          columns={orderColumns(columns, presets.orderedFields)}
          defaultSort={{ field: 'created_ts', sort: 'desc' }}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(id) => navigate(aiChangeHref(id))}
          emptyMessage="No AI change sets yet. They appear here when an MCP client, the console agent, a design import or a translation sync applies changes."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Change-set id, reason or summary',
            exportFileName: 'ai-changes',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
